"use client";

import { useState } from "react";
import { Loader2, ThumbsDown, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { PopoverAnchor } from "@radix-ui/react-popover";
import { Popover, PopoverContent } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import {
  commentOnAiFeedback,
  rateAiOutput,
  type AiFeedbackInput,
} from "./actions";

/**
 * Thumbs up/down on something the assistant produced. A thumbs down asks,
 * optionally, what was wrong. Votes can be changed; each change updates the
 * same feedback row. Remount (key) it when the rated output changes.
 *
 * Off the live inbox (the wireframe) it only keeps the vote on screen.
 */
export function AiFeedback({
  live,
  input,
  className,
}: {
  live: boolean;
  input: Omit<AiFeedbackInput, "helpful">;
  className?: string;
}) {
  const [vote, setVote] = useState<"up" | "down" | null>(null);
  const [id, setId] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [comment, setComment] = useState("");
  const [commented, setCommented] = useState(false);

  const rate = async (next: "up" | "down") => {
    if (busy || next === vote) {
      if (next === "down") setAsking(true);
      return;
    }
    const previous = vote;
    setVote(next);
    setAsking(next === "down");
    if (!live) return;

    setBusy(true);
    const result = await rateAiOutput({ ...input, helpful: next === "up" }, id);
    setBusy(false);
    if (!result.success || !result.data) {
      setVote(previous);
      setAsking(false);
      toast.error(result.error ?? "Could not save your feedback");
      return;
    }
    setId(result.data);
  };

  const sendComment = async () => {
    const text = comment.trim();
    if (!text) return setAsking(false);
    if (live) {
      if (!id) return;
      setBusy(true);
      const result = await commentOnAiFeedback(id, text);
      setBusy(false);
      if (!result.success) {
        toast.error(result.error ?? "Could not save your comment");
        return;
      }
    }
    setCommented(true);
    setAsking(false);
    toast.success("Thanks — your feedback helps tune the assistant");
  };

  const thumb = (kind: "up" | "down") => {
    const Icon = kind === "up" ? ThumbsUp : ThumbsDown;
    const active = vote === kind;
    return (
      <button
        type="button"
        onClick={() => rate(kind)}
        disabled={busy}
        aria-pressed={active}
        title={kind === "up" ? "Helpful" : "Not helpful"}
        className={cn(
          "rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50",
          active && kind === "up" && "text-emerald-600 hover:text-emerald-700",
          active && kind === "down" && "text-red-600 hover:text-red-700",
        )}
      >
        <Icon className={cn("h-3.5 w-3.5", active && "fill-current")} />
      </button>
    );
  };

  return (
    <Popover open={asking} onOpenChange={setAsking}>
      <PopoverAnchor asChild>
        <span className={cn("inline-flex items-center gap-0.5", className)}>
          {thumb("up")}
          {thumb("down")}
        </span>
      </PopoverAnchor>
      <PopoverContent align="end" className="w-72 space-y-2 p-3 text-[13px]">
        <div className="font-medium">What was wrong?</div>
        <Textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          maxLength={2000}
          rows={3}
          autoFocus
          placeholder={
            commented ? "Update your comment…" : "Optional — e.g. wrong price"
          }
          className="text-[13px]"
        />
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => setAsking(false)}
          >
            Skip
          </Button>
          <Button
            size="sm"
            className="h-7 gap-1.5 bg-primary text-xs hover:bg-primary/90"
            disabled={busy || !comment.trim()}
            onClick={sendComment}
          >
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Send
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
